import { useRef } from 'react';

/**
 * Remembers window.scrollY per view key across in-page "tabs" or toggles that
 * swap content on a single continuously-scrolling page (a TabStrip, a
 * trash/active list toggle, …). Content height often varies a lot between
 * views, so switching to a shorter one lets the browser clamp scrollY to fit
 * it; switching back then leaves you scrolled near the top of a now-tall page
 * instead of back where you actually were, forcing a manual re-scroll.
 *
 * `remember(key)` must be called synchronously in the event handler that
 * triggers the view change, BEFORE the state update that swaps the content —
 * reading scrollY after the new (often shorter) view has already rendered
 * returns the already-clamped value, not the position worth restoring.
 * `restore(key)` is meant to run from a `useLayoutEffect` keyed on the active
 * view, once that view's content has committed.
 */
export function useScrollMemory() {
  const positions = useRef(new Map<string, number>());

  return {
    remember: (key: string) => {
      positions.current.set(key, window.scrollY);
    },
    restore: (key: string) => {
      const y = positions.current.get(key);
      if (y !== undefined) window.scrollTo({ top: y });
    },
  };
}
